import { RefObject, useEffect } from 'react';
import { useFirstMountState } from 'react-use';
import { TabsProps } from 'src/components/primitives/Tabs/types';
import { Z_INDEX } from 'src/constants/zIndex';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cj } from 'src/utils/classNames';

interface Props<T extends string, S extends string> extends Required<
	Pick<TabsProps<T, S>, 'variant' | 'indicatorTrack' | 'size'>
> {
	activeIndex: number;
	ref: RefObject<HTMLDivElement | null>;
}

// Even though the tabs have `z-1`, `-z-1` here is still required by Safari for some reason -_-.
const STYLE = {
	zIndex: Z_INDEX.tab * -1,
};

export default function TabIndicator<T extends string, S extends string>({
	activeIndex,
	variant,
	indicatorTrack,
	size,
	ref,
}: Props<T, S>) {
	const isFirstMount = useFirstMountState();
	const isMobileScreenSize = useIsMobileScreenSize();

	useEffect(() => {
		const tabEl = ref.current;

		if (!tabEl) {
			return;
		}

		const update = () => {
			requestAnimationFrame(() => {
				const activeChild = tabEl.childNodes[activeIndex];

				if (!(activeChild instanceof HTMLElement)) {
					return;
				}

				const { offsetLeft, clientWidth, clientHeight } = activeChild;

				tabEl.style.setProperty('--active-offset', `${offsetLeft}px`);
				tabEl.style.setProperty('--active-height', `${clientHeight}px`);
				tabEl.style.setProperty('--active-width', `${clientWidth}px`);
			});
		};

		const resizeObserver = new ResizeObserver(update);

		for (const child of tabEl.children) {
			if (child instanceof HTMLElement) {
				resizeObserver.observe(child);
			}
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, [
		activeIndex,
		// This isn't used within the effect but we want to re-run if this has changed.
		// Otherwise, it's possible that the indicator will be misaligned after change.
		isMobileScreenSize,
	]);

	const variantClassName = {
		primary: 'bg-button-primary-bg',
		secondary: 'rounded-sm bg-button-secondary-bg',
	}[variant];

	const sizeClassName = {
		sm: 'h-px',
		md: 'h-0.5',
		lg: 'h-[3px]',
	}[size];

	return (
		<>
			{/* Track the primary indicator sits in if specified. Note, should remain */}
			{/* before the indicator in the DOM, otherwise it will cover the indicator. */}
			{indicatorTrack && (
				<span
					className={cj(
						'absolute right-0 bottom-0 left-0 bg-stroke-divider',
						sizeClassName
					)}
					style={STYLE}
				/>
			)}
			<span
				className={cj(
					'absolute bottom-0 w-full',
					'max-w-(--active-width) translate-x-(--active-offset)',
					// Avoids animating the initial tab getting set when it's not the first tab.
					!isFirstMount && 'duration-200 ease-out-smooth',
					variant === 'secondary' ? 'h-(--active-height)' : sizeClassName,
					variantClassName
				)}
				style={STYLE}
			/>
		</>
	);
}
